import { createSlice } from '@reduxjs/toolkit'
import { register } from '../../../../../helpers/reducerRegistry'

import {
  handleFinancialStatementHeader,
  handleFinancialStatementIds,
  handleTabData,
} from '../../../helpers'
import { getFinancialStatements, getYearReports } from './thunk'

const slice = createSlice({
  name: 'companyAnalysis/peerAnalysis/financialStatements',
  initialState: {
    isLoading: true,
    activeTab: 1,
    data: [],
    tableHeader: [],

    balanceSheetIds: [],
    balanceSheetData: {},

    incomeStatementIds: [],
    incomeStatementData: {},

    cashFlowStatementIds: [],
    cashFlowStatementData: {},

    isLoadingYear: false,
    yearReports: [],
    selectedYear: undefined,
  },
  reducers: {
    changeActiveTab: (state, action) => {
      state.activeTab = action.payload
    },
    changeBalanceSheetIds: (state, action) => {
      state.balanceSheetIds = action.payload
    },
    changeIncomeStatementIds: (state, action) => {
      state.incomeStatementIds = action.payload
    },
    changeCashFlowStatementIds: (state, action) => {
      state.cashFlowStatementIds = action.payload
    },
    changeSelectedYear: (state, action) => {
      state.selectedYear = action.payload
    },
  },

  extraReducers: (builder) => {
    builder.addCase(getFinancialStatements.pending, (state) => {
      state.data = []
      state.tableHeader = []
      state.balanceSheetIds = []
      state.balanceSheetData = []
      state.incomeStatementIds = []
      state.incomeStatementData = []
      state.cashFlowStatementIds = []
      state.cashFlowStatementData = []
      state.isLoading = true
    })

    builder.addCase(
      getFinancialStatements.fulfilled,
      (state, { payload = [] }) => {
        state.isLoading = false
        const data = payload
        if (!data.length) return

        state.data = data
        state.tableHeader = handleFinancialStatementHeader(data)

        // Balance Sheet
        state.balanceSheetIds = handleFinancialStatementIds(
          data,
          'balanceSheets',
        )

        state.balanceSheetData = handleTabData(
          data,
          state.tableHeader,
          state.balanceSheetIds,
          'balanceSheets',
        )

        // Income Statements
        state.incomeStatementIds = handleFinancialStatementIds(
          data,
          'incomeStatements',
        )

        state.incomeStatementData = handleTabData(
          data,
          state.tableHeader,
          state.incomeStatementIds,
          'incomeStatements',
        )

        // Cash Flows
        state.cashFlowStatementIds = handleFinancialStatementIds(
          data,
          'cashFlows',
        )
        state.cashFlowStatementData = handleTabData(
          data,
          state.tableHeader,
          state.cashFlowStatementIds,
          'cashFlows',
        )
      },
    )
    builder.addCase(getFinancialStatements.rejected, (state) => {
      state.isLoading = false
    })

    builder.addCase(getYearReports.pending, (state) => {
      state.isLoadingYear = true
    })
    builder.addCase(getYearReports.fulfilled, (state, { payload = [] }) => {
      state.isLoadingYear = false
      state.yearReports = payload.map((item) => ({
        label: item,
        value: item,
      }))
      state.selectedYear = payload.length
        ? { label: payload[0], value: payload[0] }
        : undefined
    })
    builder.addCase(getYearReports.rejected, (state, action) => {
      state.isLoadingYear = action.payload.isLoading
    })
  },
})

// common
export const selectActiveTab = (state) => state[slice.name].activeTab
export const selectData = (state) => state[slice.name].data
export const selectIsLoading = (state) => state[slice.name].isLoading
export const selectTableHeader = (state) => state[slice.name].tableHeader

// select balance sheet
export const selectBalanceSheetItem = (rowId, colId) => (state) =>
  state[slice.name].balanceSheetData[rowId][colId]
export const selectBalanceSheetRowData = (rowId) => (state) =>
  state[slice.name].balanceSheetData[rowId]
export const selectBalanceSheetData = (state) =>
  state[slice.name].balanceSheetData
export const selectBalanceSheetIds = (state) =>
  state[slice.name].balanceSheetIds
export const selectBalanceSheetChildIds = (rowId) => (state) =>
  state[slice.name].balanceSheetData[rowId].childrenIds
export const selectBalanceSheetParentId = (rowId) => (state) =>
  state[slice.name].balanceSheetData[rowId].parentId

// select income Statement
export const selectIncomeStatementItem = (rowId, colId) => (state) =>
  state[slice.name].incomeStatementData[rowId][colId]
export const selectIncomeStatementRowData = (rowId) => (state) =>
  state[slice.name].incomeStatementData[rowId]
export const selectIncomeStatementData = (state) =>
  state[slice.name].incomeStatementData
export const selectIncomeStatementIds = (state) =>
  state[slice.name].incomeStatementIds
export const selectIncomeStatementChildIds = (rowId) => (state) =>
  state[slice.name].incomeStatementData[rowId].childrenIds
export const selectIncomeStatementParentId = (rowId) => (state) =>
  state[slice.name].incomeStatementData[rowId].parentId

// select Cash FLows
export const selectCashFlowStatementItem = (rowId, colId) => (state) =>
  state[slice.name].cashFlowStatementData[rowId][colId]
export const selectCashFlowStatementData = (state) =>
  state[slice.name].cashFlowStatementData
export const selectCashFlowStatementIds = (state) =>
  state[slice.name].cashFlowStatementIds
export const selectCashFlowStatementRowData = (rowId) => (state) =>
  state[slice.name].cashFlowStatementData[rowId]
export const selectCashFlowStatementChildIds = (rowId) => (state) =>
  state[slice.name].cashFlowStatementData[rowId].childrenIds
export const selectCashFlowStatementParentId = (rowId) => (state) =>
  state[slice.name].cashFlowStatementData[rowId].parentId

export const selectIsLoadingYear = (state) => state[slice.name].isLoadingYear
export const selectYear = (state) => state[slice.name].selectedYear
export const selectYearReports = (state) => state[slice.name].yearReports

export const {
  changeActiveTab,
  changeBalanceSheetIds,
  changeIncomeStatementIds,
  changeCashFlowStatementIds,
  changeSelectedYear,
} = slice.actions

register(slice.name, slice.reducer)
